/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleSystemLegalLink.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import SlidingComponentLinkTemplate from "./SlidingComponentLinkTemplate";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../../redux/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CustomSearchbarWithApiV2 from "../../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import { componentStyle } from "../../../config/appTheme";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";

function VehicleSystemLegalLink({
    triggerOnSelectVehicleSystem,
    selectedVehicleSystem,
    setSelectedVehicleSystem,
    setTotalVehicleSystemCount,
}) {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const zoneFromurl = queryParams.get("zone");
    const vsFromUrl = queryParams.get("vs_id");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const selectedProjectDetails = useSelector(
        (state) => state?.projectConfigReducer?.selectedProjectDetails,
    );
    const selectedSingleVariantDetails = useSelector(
        (state) => state.projectConfigReducer.selectedVariantDetails,
    );

    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(25);
    const [vsCount, setVsCount] = useState(0);
    const [vsMainList, setVsMainList] = useState([]);
    const [modifiedVSList, setModifiedVSList] = useState([]);
    const [selectedProjectIsZonal, setSelectedProjectIsZonal] = useState(false);
    const [selectedProjectZonesList, setSelectedProjectZonesList] = useState([]);
    const [zoneMainOptionsList, setZoneMainOptionsList] = useState([]);
    const [selectedZoneMain, setSelectedZoneMain] = useState(null);

    const [projectID, setProjectID] = useState(null);
    const [variantID, setVariantID] = useState(null);
    const [selectedMainVSDetails, setSelectedMainVSDetails] = useState(null);
    const [linkedLegalRequirement, setLinkedLegalRequirement] = useState([]);
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
    const [pendingVSActionDialogOpen, setPendingVSActionDialogOpen] =
        useState(false);
    const [selectedPendingVSAction, setSelectedPendingVSAction] = useState(null);
    const [vsGetTrigger, setVsGetTrigger] = useState(false);
    const [cardOpen, setCardOpen] = useState(true);
    const [disableActionButton, setDisableActionButton] = useState(false);

    useEffect(() => {
        setSelectedProjectIsZonal(selectedProjectDetails?.is_zonal);
        setSelectedProjectZonesList(selectedProjectDetails?.project_zones);
        if (
            selectedProjectDetails?.project_id &&
            selectedProjectDetails?.project_id !== projectID
        ) {
            setVariantID(null);
            setProjectID(selectedProjectDetails?.project_id);
        }
    }, [selectedProjectDetails, projectID]);

    useEffect(() => {
        setVsMainList([]);
        if (
            selectedSingleVariantDetails?.variant_id &&
            selectedSingleVariantDetails?.variant_id !== variantID
        ) {
            setVariantID(selectedSingleVariantDetails?.variant_id);
        }
    }, [selectedSingleVariantDetails, variantID]);

    useEffect(() => {
        if (selectedProjectIsZonal) {
            const filteredZoneMainOptionsList = selectedProjectZonesList
                ?.map((el) => ({ value: el?.position, label: el?.zone_name }))
                ?.sort((a, b) => a.label.localeCompare(b.label));
            setZoneMainOptionsList(filteredZoneMainOptionsList);

            const zoneFromUrlValue = filteredZoneMainOptionsList?.find(
                (zone) =>
                    String(zone.value) === String(zoneFromurl) ||
                    String(zone.label) === String(zoneFromurl),
            );

            if (zoneFromUrlValue) {
                setSelectedZoneMain(zoneFromUrlValue?.value);
            } else if (
                selectedProjectIsZonal &&
                filteredZoneMainOptionsList?.[0]?.value &&
                !selectedZoneMain
            ) {
                setSelectedZoneMain(filteredZoneMainOptionsList?.[0]?.value);
            } else if (selectedProjectIsZonal) {
                setSelectedZoneMain(filteredZoneMainOptionsList?.[0]?.value);
            } else {
                setSelectedZoneMain(null);
            }
        }
    }, [selectedProjectZonesList, selectedProjectIsZonal, selectedZoneMain, zoneFromurl]);

    useEffect(() => {
        if (zoneFromurl) {
            setSelectedZoneMain(zoneFromurl);
        }
        if (!vsFromUrl) {
            setSelectedMainVSDetails(null);
            setLinkedLegalRequirement([]);
            setVsGetTrigger((prev) => !prev);
        }
    }, [zoneFromurl, vsFromUrl]);

    const getLegalRequirement = useCallback(async (vs_id) => {
        if (!vs_id) return;

        const resp = await Promise.resolve(dispatch(
            HandleApiActions({
                ...softwareIntersectionAPI.get_vs_legal_requirements,
                params: {
                    vs_id,
                    project_id: selectedProjectDetails?.project_id,
                    variant_id: selectedSingleVariantDetails?.variant_id,
                },
            }),
        ));

        const linkedRequirements = resp?.data?.[0]?.linked_lr_details;
        if (resp?.error || !linkedRequirements?.[0]?.lr_id) {
            setLinkedLegalRequirement([]);
            return;
        }

        setLinkedLegalRequirement(
            linkedRequirements.map(({ ls_details, ...requirement }) => ({
                ...requirement,
                ...ls_details,
            })),
        );
    }, [dispatch, selectedProjectDetails?.project_id, selectedSingleVariantDetails?.variant_id]);

    const handleSelectedComponent = useCallback((selectedObj) => {
        setSelectedMainVSDetails(selectedObj);
        setSelectedVehicleSystem(selectedObj);
        triggerOnSelectVehicleSystem?.(selectedObj?.id);
        getLegalRequirement(Number(selectedObj?.id));
    }, [getLegalRequirement, triggerOnSelectVehicleSystem, setSelectedVehicleSystem]);

    useEffect(() => {
        setLinkedLegalRequirement([]);
        const modifiedList = vsMainList.map((item) => ({
            data: item,
            id: item.id,                   // ✅ API returns "id"
            cardDataArray: [
                {
                    key: "Assembly Number",
                    value: item.assembly_number ?? "", // ✅ real field, fallback to ""
                    keyStyle: { fontWeight: 600 },
                    valueStyle: { fontWeight: 600 },
                },
                {
                    key: "Name",
                    value: item.name ?? "",           // ✅ real field
                },
                {
                    key: "Description",
                    value: item.description ?? "",    // ✅ real field, fallback prevents crash
                },
            ],
        }));
        setModifiedVSList(modifiedList);
        if (modifiedList.length) {
            handleSelectedComponent(modifiedList[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vsMainList]);



    const unlinkLegalRequirement = async ({ lr_id, reason, callback }) => {
        console.log("unlinkLegalRequirement placeholder", lr_id, reason);
    };

    const handleActionDialogOpen = () => setActionDialogOpen(true);
    const handleActionDialogClose = () => setActionDialogOpen(false);
    const handleActionType1DialogOpen = () => setActionType1DialogOpen(true);
    const handleActionType1DialogClose = () => setActionType1DialogOpen(false);
    const handlePendingVSActionDialogClose = () => {
        setPendingVSActionDialogOpen(false);
        setSelectedPendingVSAction(null);
    };

    const getVSActionTypeTooltip = (actionType) => {
        switch (Number(actionType)) {
            case 1:
                return "Action Pending in Vehicle System to Vehicle System linking";
            case 2:
                return "Action Pending in Vehicle System to Legal linking";
            case 4:
                return "Action required in Vehicle System Attribute";
            case 5:
                return "Action pending in Vehicle System Version";
            default:
                return "";
        }
    };

    const handleVSActionTypeClick = (event, item) => {
        event.stopPropagation();
        if ([4, 5].includes(Number(item?.action_type))) {
            setSelectedPendingVSAction(item);
            setPendingVSActionDialogOpen(true);
            return;
        }
        if (Number(item?.action_type) === 2) {
            handleActionDialogOpen();
        } else if (Number(item?.action_type) === 1) {
            handleActionType1DialogOpen();
        }
    };

    const handleRedirectionToVSAction = () => {
        const selectedVS = selectedPendingVSAction || selectedMainVSDetails?.data;
        const selectedZoneDetails = selectedProjectZonesList?.find(
            (zone) => Number(zone?.position) === Number(selectedZoneMain),
        );
        const params = new URLSearchParams();
        if (selectedVS?.id) params.set("vs_id", selectedVS.id);

        params.set("actionMode", "vsAction");
        if (selectedZoneMain) {
            params.set(
                "selectedZone",
                selectedZoneDetails?.zone_name || selectedZoneMain,
            );
            params.set("zonePosition", selectedZoneMain);
        }
        if (page) params.set("page", page);

        navigate(`/dashboard/v2/vehicle-system?${params.toString()}`);
        handlePendingVSActionDialogClose();
    };

    const confirmUpdate = async () => {
        const reqObj = {
            current_time: new Date().toISOString(),
            project_id: Number(projectID),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            vs_id: Number(selectedMainVSDetails?.id),
            is_dependency_linking: false,
            module_type: "VS_LR",
        };
        try {
            setDisableActionButton(true);
            console.log("confirmUpdate placeholder", reqObj);
            handleActionDialogClose();
            navigate(`/dashboard/v2/type-approval/link?zone=${selectedZoneMain}`);
        } catch (error) {
            console.error("Failed to update action type", error);
        }
        setDisableActionButton(false);
    };

    const updateChangesAndClose = () => {
        handleActionDialogClose();
        navigate(`/dashboard/v2/type-approval/link?zone=${selectedZoneMain}`);
    };

    const searchParams = useMemo(() => {
        if (!projectID && !variantID) return null;
        return {
            project_id: Number(projectID),
            variant_id: Number(variantID),
            ...(selectedProjectIsZonal && { zone_position: selectedZoneMain }),
            ...(vsFromUrl && { vs_id: Number(vsFromUrl) }),
            status: "true",
            page_number: page,
            page_size: rowsPerPage,
        };
    }, [projectID, variantID, selectedProjectIsZonal, selectedZoneMain, page, rowsPerPage, vsFromUrl]);

    return (
        <Box>
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", pr: 1.4 }}>
                <CustomButtonV2
                    disabled={!projectID || !variantID}
                    btnName="Create Ticket"
                    handleOnClick={() => {
                        navigate("/dashboard/v2/type-approval/link/create?tab=0");
                    }}
                />
            </Box>

            <SlidingComponentLinkTemplate
                cardOpen={cardOpen}
                setCardOpen={setCardOpen}
                contentHeight={"20rem"}
                headerTitle={
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 0.5,
                            }}
                        >
                            <Typography>Select Vehicle System</Typography>
                            <Box onClick={() => setCardOpen(false)} sx={{ cursor: "pointer" }}>
                                <KeyboardArrowUpIcon />
                            </Box>
                        </Box>
                        <Box
                            display="flex"
                            gap={1}
                            sx={{
                                width: "100%",
                                mt: 0.5,
                                ml: -0.5,
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Box sx={{ flex: selectedProjectIsZonal ? "0 0 60%" : "1" }}>
                                {projectID && variantID && (
                                    <CustomSearchbarWithApiV2
                                        key={`vs-search-${projectID}-${variantID}-${selectedZoneMain}`}
                                        apiAction={labCar.get_vehicle_systems}
                                        searchKey={"assembly_number"}

                                        placeholder={"Search Vehicle System"}
                                        triggerApi={vsGetTrigger}
                                        getDataOnSearch={(data, resp) => {
                                            if (resp.error) {
                                                setVsMainList([]);
                                            } else {
                                                setVsMainList(resp.data);
                                                setVsCount(resp.total_count);
                                                setTotalVehicleSystemCount(resp.total_count);
                                            }
                                        }}
                                        params={searchParams}
                                        skipApiCall={
                                            !projectID ||
                                            !variantID ||
                                            (selectedProjectIsZonal && !selectedZoneMain)
                                        }
                                        iconColor={"white"}
                                        txtFldStyle={componentStyle.searchField.textField}
                                        skipInitialSearch={false}
                                    />
                                )}
                            </Box>
                            {selectedProjectIsZonal && (
                                <Box sx={{ flex: "0 0 40%" }}>
                                    <CustomSelectV2
                                        label={"Select Zone"}
                                        optionsList={zoneMainOptionsList}
                                        selectedValue={selectedZoneMain}
                                        handleOnChange={(e) => setSelectedZoneMain(e.target.value)}
                                        dark={true}
                                        showLabel={false}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                }
                linkedLegalRequirement={linkedLegalRequirement}
                unlinkLegalRequirement={unlinkLegalRequirement}
                searchKeys={["chapter", "name", "standard_number", "ls_id", "req_number"]}
                dataList={modifiedVSList}
                handleSelectedComponent={handleSelectedComponent}
                selectedComponentDetails={selectedMainVSDetails}
                renderCardActionIcons={(item) => (
                    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        {[1, 2, 4, 5].includes(Number(item.action_type)) && (
                            <Box sx={{ position: "absolute", top: "8px", right: "8px" }}>
                                <Tooltip title={getVSActionTypeTooltip(item.action_type)}>
                                    <IconButton
                                        size="small"
                                        sx={{ color: "black" }}
                                        onClick={(event) => handleVSActionTypeClick(event, item)}
                                    >
                                        <ErrorOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                )}
                page={page}
                rowsPerPage={rowsPerPage}
                handlePageChange={(selectedPage) => setPage(selectedPage)}
                totalCount={vsCount}
            />

            <CustomDialogBoxV2
                open={actionDialogOpen}
                onClose={() => setActionDialogOpen(false)}
                title={"Actions"}
                successButtonName={"Yes"}
                rejectButtonName={"No"}
                actionButtonStyle={"continuous"}
                onClickSuccess={confirmUpdate}
                onClickReject={updateChangesAndClose}
                disabled={disableActionButton}
            >
                <Box sx={{ width: "300px" }}>
                    <Typography sx={{ ...componentStyle.font.text }}>
                        Are you sure the intersection for Vehicle System is completed?
                    </Typography>
                </Box>
            </CustomDialogBoxV2>

            <CustomDialogBoxV2
                open={actionType1DialogOpen}
                onClose={handleActionType1DialogClose}
                title={"Actions"}
                successButtonName={"Link Now"}
                rejectButtonName={"Link Later"}
                actionButtonStyle={"continuous"}
                onClickSuccess={() => {
                    navigate(
                        `/dashboard/v2/link?project_id=${projectID}&variant_id=${Number(
                            selectedSingleVariantDetails?.variant_id,
                        )}&vs_id=${selectedMainVSDetails?.id}&selectedZone=${selectedZoneMain}&linkVehicleComponentsScreen=${true}`,
                    );
                    handleActionType1DialogClose();
                }}
                onClickReject={handleActionType1DialogClose}
            >
                <Box sx={{ width: "300px" }}>
                    <Typography sx={{ ...componentStyle.font.text }}>
                        Vehicle System dependency linking is pending!
                    </Typography>
                </Box>
            </CustomDialogBoxV2>

            <CustomDialogBoxV2
                open={pendingVSActionDialogOpen}
                onClose={handlePendingVSActionDialogClose}
                title={"Action"}
                successButtonName={"Link Now"}
                rejectButtonName={"Link Later"}
                actionButtonStyle={"continuous"}
                onClickSuccess={handleRedirectionToVSAction}
                onClickReject={handlePendingVSActionDialogClose}
            >
                <Box sx={{ width: "300px" }}>
                    <Typography sx={{ ...componentStyle.font.text }}>
                        {Number(selectedPendingVSAction?.action_type) === 4
                            ? "Action pending in Vehicle System Attribute"
                            : "Action pending in Vehicle System Version"}
                    </Typography>
                </Box>
            </CustomDialogBoxV2>
        </Box>
    );
}

VehicleSystemLegalLink.propTypes = {
    triggerOnSelectVehicleSystem: PropTypes.func.isRequired,
    selectedVehicleSystem: PropTypes.any,
    setSelectedVehicleSystem: PropTypes.func.isRequired,
    setTotalVehicleSystemCount: PropTypes.func.isRequired,
};

export default VehicleSystemLegalLink;
